import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { Input } from '@/components/ui/Input';
import { SkeletonUsersPage } from '@/components/ui/Skeleton';
import { useToast } from '@/hooks/useToast';
import { api } from '@/lib/api';
import { normalizeMexicanPhone } from '@/lib/utils';
import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Eye,
  KeyRound,
  Lock,
  Mail,
  MoreVertical,
  Pencil,
  Search,
  Settings2,
  Shield,
  Trash2,
  User,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface User {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
  banned_until: string | null;
  roles: string[];
  organization_name: string | null;
  phone: string | null;
  name: string | null;
  customer_type: 'egresado_unach' | 'publico_general' | null;
  graduate_program: string | null;
}

interface UsersResponse {
  users: User[];
  total: number;
}

const PROTECTED_USER_EMAIL = 'root@sudo.su';

type SortField = 'name' | 'email' | 'organization_name' | 'last_sign_in_at' | 'created_at';
type SortDirection = 'asc' | 'desc';

const matchesSearch = (user: User, rawTerm: string) => {
  const term = rawTerm.trim().toLowerCase();
  if (!term) return true;

  return (
    user.email.toLowerCase().includes(term) ||
    user.name?.toLowerCase().includes(term) ||
    user.organization_name?.toLowerCase().includes(term)
  );
};

export default function UsersManagementPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'client' | 'organizer' | 'superadmin'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showStatusMenu, setShowStatusMenu] = useState(false);

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState<string | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

  // Modal para crear/editar usuario
  const [showUserModal, setShowUserModal] = useState(false);
  const [userFormData, setUserFormData] = useState({
    id: '',
    email: '',
    password: '',
    name: '',
    phone: '',
    organization_name: '',
    role: 'organizer',
    isEdit: false,
    sendEmail: true,
  });

  // Modal para resetear contraseña
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({
    userId: '',
    userEmail: '',
    newPassword: '',
    sendEmail: true,
  });

  const [showGeneratedPasswordModal, setShowGeneratedPasswordModal] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [generatedPasswordUserEmail, setGeneratedPasswordUserEmail] = useState('');

  // Modal para corregir correo en cascada
  const [showEmailCascadeModal, setShowEmailCascadeModal] = useState(false);
  const [emailCascadeData, setEmailCascadeData] = useState({
    oldEmail: '',
    newEmail: '',
    contextUserEmail: '',
  });

  const { success, error: showError } = useToast();

  const loadUsers = useCallback(async (): Promise<User[] | null> => {
    try {
      setIsLoading(true);
      const data = await api.get<UsersResponse>('/users');
      setUsers(data.users);
      return data.users;
    } catch (err) {
      console.error('Error loading users:', err);
      showError(
        'Error al cargar usuarios: ' + (err instanceof Error ? err.message : 'Error desconocido')
      );
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);
  // Aplicar filtros cuando cambien
  useEffect(() => {
    applyFilters();
  }, [users, filter, searchTerm, statusFilter, sortField, sortDirection]); // Agregado statusFilter explícitamente si faltaba, aunque ya debería estar cubierto por la lógica de render.

  const applyFilters = () => {
    let result = [...users];

    // Filtro por rol
    if (filter !== 'all') {
      result = result.filter((user) => user.roles.includes(filter));
    }

    // Filtro por estado (activo/inactivo)
    if (statusFilter !== 'all') {
      result = result.filter((user) => {
        const isActive = !user.banned_until;
        if (statusFilter === 'active') return isActive;
        if (statusFilter === 'inactive') return !isActive;
        return true;
      });
    }

    // Filtro por búsqueda
    result = result.filter((user) => matchesSearch(user, searchTerm));

    // Ordenamiento
    result.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      // Manejar valores nulos
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Convertir a string para comparación
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredUsers(result);
    setCurrentPage(1); // Reset a la primera página cuando cambian filtros
  };

  const isProtectedUser = (email: string) => email === PROTECTED_USER_EMAIL;

  const { allCount, superAdminCount, managerCount, accountantCount } = useMemo(() => {
    let base = [...users];

    if (statusFilter !== 'all') {
      base = base.filter((user) => {
        const isActive = !user.banned_until;
        if (statusFilter === 'active') return isActive;
        if (statusFilter === 'inactive') return !isActive;
        return true;
      });
    }

    base = base.filter((user) => matchesSearch(user, searchTerm));

    return {
      allCount: base.length,
      superAdminCount: base.filter((u) => u.roles.includes('superadmin')).length,
      managerCount: base.filter((u) => u.roles.includes('organizer')).length,
      accountantCount: base.filter((u) => u.roles.includes('client')).length,
    };
  }, [users, searchTerm, statusFilter]);

  const exportToCSV = useCallback(() => {
    try {
      // Preparar datos para CSV
      const csvData = filteredUsers.map((user) => ({
        Email: user.email,
        Nombre: user.name || '',
        Roles: user.roles.join(', '),
        Organización: user.organization_name || '',
        Teléfono: user.phone || '',
        Estado: user.banned_until ? 'Desactivado' : 'Activo',
        'Creado el': new Date(user.created_at).toLocaleDateString('es-ES'),
        'Última sesión': user.last_sign_in_at
          ? new Date(user.last_sign_in_at).toLocaleDateString('es-ES')
          : 'Nunca',
      }));

      // Crear headers
      const headers = Object.keys(csvData[0] || {});
      const csvContent = [
        headers.join(','),
        ...csvData.map((row) =>
          headers
            .map((header) => {
              const value = String(row[header as keyof typeof row] || '');
              // Escapar comillas y comas
              return value.includes(',') || value.includes('"')
                ? `"${value.replace(/"/g, '""')}"`
                : value;
            })
            .join(',')
        ),
      ].join('\n');

      // Descargar archivo
      const blob = new Blob(['\ufeff' + csvContent], {
        type: 'text/csv;charset=utf-8;',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `usuarios_eventflow_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();

      success('Usuarios exportados correctamente');
    } catch (error) {
      showError('Error al exportar usuarios');
      console.error(error);
    }
  }, [filteredUsers, success, showError]);

  const openCreateUserModal = useCallback(() => {
    setUserFormData({
      id: '',
      email: '',
      password: '',
      name: '',
      phone: '',
      organization_name: '',
      role: 'organizer',
      isEdit: false,
      sendEmail: true,
    });
    setShowUserModal(true);
  }, []);

  useEffect(() => {
    const handleSearchSet = (event: Event) => {
      const detail = (event as CustomEvent<{ term?: string }>).detail;
      setSearchTerm(detail?.term ?? '');
    };

    const handleOpenUser = (event: Event) => {
      const detail = (event as CustomEvent<{ userId?: string }>).detail;
      const userId = detail?.userId;
      if (!userId) return;
      const found = users.find((u) => u.id === userId);
      if (found) {
        setSelectedUser(found);
        return;
      }

      void (async () => {
        const fetched = await loadUsers();
        const after = fetched?.find((u) => u.id === userId);
        if (after) setSelectedUser(after);
      })();
    };

    const handleOpenCreate = () => openCreateUserModal();
    const handleExport = () => exportToCSV();
    const handleReload = () => void loadUsers();

    window.addEventListener('users:search:set', handleSearchSet as EventListener);
    window.addEventListener('users:open', handleOpenUser as EventListener);
    window.addEventListener('users:open-create-modal', handleOpenCreate);
    window.addEventListener('users:export-csv', handleExport);
    window.addEventListener('users:reload', handleReload);

    return () => {
      window.removeEventListener('users:search:set', handleSearchSet as EventListener);
      window.removeEventListener('users:open', handleOpenUser as EventListener);
      window.removeEventListener('users:open-create-modal', handleOpenCreate);
      window.removeEventListener('users:export-csv', handleExport);
      window.removeEventListener('users:reload', handleReload);
    };
  }, [exportToCSV, loadUsers, openCreateUserModal, users]);

  const openEditUserModal = (user: User) => {
    if (isProtectedUser(user.email)) {
      showError('No se puede editar al usuario root');
      return;
    }

    setUserFormData({
      id: user.id,
      email: user.email,
      password: '',
      name: user.name || '',
      phone: user.phone || '',
      organization_name: user.organization_name || '',
      role: user.roles.includes('organizer') ? 'organizer' : user.roles[0] || 'client',
      isEdit: true,
      sendEmail: false,
    });
    setShowUserModal(true);
    setShowActionsMenu(null);
  };

  const openResetPasswordModal = (user: User) => {
    if (isProtectedUser(user.email)) {
      showError('No se puede resetear la contraseña del usuario root');
      return;
    }

    setResetPasswordData({
      userId: user.id,
      userEmail: user.email,
      newPassword: '',
      sendEmail: true,
    });
    setShowResetPasswordModal(true);
    setShowActionsMenu(null);
  };

  const openEmailCascadeModal = (user: User) => {
    if (isProtectedUser(user.email)) {
      showError('No se puede ejecutar la cascada para el usuario root');
      return;
    }

    setEmailCascadeData({
      oldEmail: user.email,
      newEmail: '',
      contextUserEmail: user.email,
    });
    setShowEmailCascadeModal(true);
    setShowActionsMenu(null);
  };

  const handleSaveUser = async () => {
    if (!userFormData.email) {
      showError('El email es requerido');
      return;
    }

    if (!userFormData.isEdit && !userFormData.password) {
      showError('La contraseña es requerida para nuevos usuarios');
      return;
    }

    // Normalizar teléfono si está presente (10 dígitos, solo números)
    let normalizedPhone: string | null = null;
    if (userFormData.phone && userFormData.phone.trim()) {
      normalizedPhone = normalizeMexicanPhone(userFormData.phone);
      if (!normalizedPhone) {
        showError(
          'El teléfono debe tener exactamente 10 dígitos (solo números, sin espacios ni caracteres especiales)'
        );
        return;
      }
    }

    setIsProcessing(true);
    try {
      if (userFormData.isEdit) {
        // Actualizar usuario existente
        const body: any = {
          email: userFormData.email,
          name: userFormData.name,
          phone: normalizedPhone,
        };
        if (userFormData.role === 'organizer') {
          body.organization_name = userFormData.organization_name;
        }
        await api.put(`/users/${userFormData.id}`, body);
        success('Usuario actualizado correctamente');
      } else {
        // Crear nuevo usuario
        const body: any = {
          email: userFormData.email,
          password: userFormData.password,
          name: userFormData.name,
          phone: normalizedPhone,
          role: userFormData.role,
          sendEmail: userFormData.sendEmail,
        };
        if (userFormData.role === 'organizer') {
          body.organization_name = userFormData.organization_name;
        }
        await api.post('/users', body);
        success(
          userFormData.sendEmail
            ? 'Usuario creado y credenciales enviadas por email'
            : 'Usuario creado correctamente'
        );
      }

      setShowUserModal(false);
      await loadUsers();
    } catch (err) {
      showError('Error: ' + (err instanceof Error ? err.message : 'Error desconocido'));
    } finally {
      setIsProcessing(false);
    }
  };

  // Mostrar contraseña actual (sin resetear)
  const handleShowCurrentPassword = async (userId: string, userEmail: string) => {
    setIsProcessing(true);
    try {
      const response = await api.get<{
        password: string | null;
        hasPassword: boolean;
        message?: string;
        updatedAt?: string;
      }>(`/users/${userId}/password`);

      if (response.hasPassword && response.password) {
        // Mostrar la contraseña guardada
        setGeneratedPassword(response.password);
        setGeneratedPasswordUserEmail(userEmail);
        setShowGeneratedPasswordModal(true);
      } else {
        // No hay contraseña guardada, preguntar si quiere generar una nueva
        if (
          confirm(
            `No hay contraseña guardada para ${userEmail}.\n\n¿Desea generar una nueva contraseña?`
          )
        ) {
          handleResetPassword(true, userId);
        }
      }
    } catch (err) {
      showError('Error: ' + (err instanceof Error ? err.message : 'Error desconocido'));
    } finally {
      setIsProcessing(false);
    }
    setShowActionsMenu(null);
  };

  const handleResetPassword = async (showPassword = false, userIdOverride?: string) => {
    // Usar userIdOverride si se proporciona (para "Mostrar Pass"), sino usar el del estado
    const targetUserId = userIdOverride || resetPasswordData.userId;

    // Validar que tenemos un userId
    if (!targetUserId) {
      showError('Error: ID de usuario no encontrado');
      return;
    }

    // Validaciones para modo manual (con contraseña ingresada)
    if (!showPassword) {
      if (!resetPasswordData.newPassword) {
        showError('La nueva contraseña es requerida');
        return;
      }

      if (resetPasswordData.newPassword.length < 6) {
        showError('La contraseña debe tener al menos 6 caracteres');
        return;
      }
    }

    setIsProcessing(true);
    try {
      // Preparar el body: incluir newPassword solo si se proporciona (modo manual)
      const requestBody: { sendEmail: boolean; newPassword?: string } = {
        sendEmail: showPassword ? false : resetPasswordData.sendEmail,
      };

      // Solo enviar newPassword si no es modo "mostrar pass" (generar automático)
      if (!showPassword && resetPasswordData.newPassword) {
        requestBody.newPassword = resetPasswordData.newPassword;
      }

      const response = await api.post<{ message: string; newPassword: string }>(
        `/users/${targetUserId}/reset-password`,
        requestBody
      );

      // Si es modo "mostrar pass", mostrar la contraseña generada
      if (showPassword && response.newPassword) {
        setGeneratedPassword(response.newPassword);
        // Guardar el email del usuario para mostrarlo en el modal
        const userEmail = userIdOverride
          ? users.find((u) => u.id === userIdOverride)?.email || resetPasswordData.userEmail
          : resetPasswordData.userEmail;
        setGeneratedPasswordUserEmail(userEmail);
        setShowGeneratedPasswordModal(true);
        setShowResetPasswordModal(false);
      } else {
        // Modo manual: mostrar mensaje de éxito
        success(
          resetPasswordData.sendEmail
            ? 'Contraseña reseteada y enviada por email'
            : 'Contraseña reseteada correctamente'
        );
        setShowResetPasswordModal(false);
      }

      // Limpiar estado
      setResetPasswordData({
        userId: '',
        userEmail: '',
        newPassword: '',
        sendEmail: true,
      });
    } catch (err) {
      showError('Error: ' + (err instanceof Error ? err.message : 'Error desconocido'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (isProtectedUser(userEmail)) {
      showError('No se puede eliminar al usuario root');
      return;
    }

    if (!confirm('¿Eliminar este usuario? Esta acción no se puede deshacer.')) {
      return;
    }

    setIsProcessing(true);
    try {
      await api.delete(`/users/${userId}`);
      success('Usuario eliminado correctamente');
      await loadUsers();
      if (selectedUser?.id === userId) {
        setSelectedUser(null);
      }
    } catch (err) {
      showError(
        'Error al eliminar usuario: ' + (err instanceof Error ? err.message : 'Error desconocido')
      );
    } finally {
      setIsProcessing(false);
    }
    setShowActionsMenu(null);
  };

  const handleEmailCascadeFix = async () => {
    console.log('[EmailCascade] handleEmailCascadeFix called', emailCascadeData);
    const { oldEmail, newEmail } = emailCascadeData;
    if (!oldEmail) {
      showError('No se encontró el email actual del usuario');
      return;
    }

    if (!newEmail) {
      showError('Debes ingresar el email nuevo');
      return;
    }

    if (oldEmail === newEmail) {
      showError('El email anterior y el nuevo no pueden ser iguales');
      return;
    }

    console.log('[EmailCascade] Sending request...', { oldEmail, newEmail });
    setIsProcessing(true);
    try {
      const response = await api.post<{
        message: string;
        cascade: {
          updatedPurchases: number;
          updatedEmailLogs: number;
          backfilledRecipientNames: number;
        };
      }>('/users/email-cascade', { oldEmail, newEmail });

      console.log('[EmailCascade] Response:', response);
      const c = response.cascade;
      success(
        `Cascada ejecutada. purchases: ${c.updatedPurchases}, email_logs: ${c.updatedEmailLogs}, names backfill: ${c.backfilledRecipientNames}`
      );
      setShowEmailCascadeModal(false);
      // recargar por si cambió algo visible
      await loadUsers();
    } catch (err) {
      console.error('[EmailCascade] Error:', err);
      showError(
        'Error al ejecutar cascada: ' + (err instanceof Error ? err.message : 'Error desconocido')
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAssignRole = async (userId: string, roleName: string) => {
    if (!confirm(`¿Asignar rol "${roleName}" a este usuario?`)) return;

    setIsProcessing(true);
    try {
      await api.post(`/users/${userId}/roles`, { roleName });
      success(`Rol ${roleName} asignado correctamente`);
      await loadUsers();
      if (selectedUser?.id === userId) {
        const updatedUser = users.find((u) => u.id === userId);
        if (updatedUser) setSelectedUser(updatedUser);
      }
    } catch (err) {
      showError('Error: ' + (err instanceof Error ? err.message : 'Error desconocido'));
    } finally {
      setIsProcessing(false);
    }
    setShowActionsMenu(null);
  };

  const handleRemoveRole = async (userId: string, roleName: string) => {
    if (!confirm(`¿Quitar rol "${roleName}" a este usuario?`)) return;

    setIsProcessing(true);
    try {
      await api.delete(`/users/${userId}/roles/${roleName}`);
      success(`Rol ${roleName} removido correctamente`);
      await loadUsers();
      if (selectedUser?.id === userId) {
        const updatedUser = users.find((u) => u.id === userId);
        if (updatedUser) setSelectedUser(updatedUser);
      }
    } catch (err) {
      showError('Error: ' + (err instanceof Error ? err.message : 'Error desconocido'));
    } finally {
      setIsProcessing(false);
    }
    setShowActionsMenu(null);
  };

  const handleToggleUserStatus = async (
    userId: string,
    userEmail: string,
    action: 'ban' | 'unban'
  ) => {
    if (isProtectedUser(userEmail)) {
      showError('No se puede desactivar al usuario root');
      return;
    }

    const message =
      action === 'ban'
        ? '¿Desactivar este usuario? No podrá iniciar sesión.'
        : '¿Activar este usuario? Podrá iniciar sesión nuevamente.';

    if (!confirm(message)) return;

    setIsProcessing(true);
    try {
      await api.patch(`/users/${userId}/status`, { action });
      success(action === 'ban' ? 'Usuario desactivado' : 'Usuario activado');
      await loadUsers();
      if (selectedUser?.id === userId) {
        const updatedUser = users.find((u) => u.id === userId);
        if (updatedUser) setSelectedUser(updatedUser);
      }
    } catch (err) {
      showError('Error: ' + (err instanceof Error ? err.message : 'Error desconocido'));
    } finally {
      setIsProcessing(false);
    }
    setShowActionsMenu(null);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Paginación
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

  if (isLoading) {
    return <SkeletonUsersPage />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-gray-500" />
              <h1 className="text-lg font-semibold text-gray-700">Cuentas de Administrador</h1>
            </div>
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-64"
                />
              </div>

              {/* Status Filter */}
              <div className="relative">
                <button
                  onClick={() => setShowStatusMenu(!showStatusMenu)}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  <span>Estatus</span>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {/* Status dropdown menu */}
                {showStatusMenu && (
                  <div className="absolute right-0 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setStatusFilter('all');
                          setShowStatusMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center justify-between ${statusFilter === 'all' ? 'text-indigo-600 font-medium' : 'text-gray-700'
                          }`}
                      >
                        Todos
                        {statusFilter === 'all' && <div className="w-1.5 h-1.5 rounded-full bg-indigo-600" />}
                      </button>
                      <button
                        onClick={() => {
                          setStatusFilter('active');
                          setShowStatusMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center justify-between ${statusFilter === 'active' ? 'text-indigo-600 font-medium' : 'text-gray-700'
                          }`}
                      >
                        Activos
                        {statusFilter === 'active' && <div className="w-1.5 h-1.5 rounded-full bg-indigo-600" />}
                      </button>
                      <button
                        onClick={() => {
                          setStatusFilter('inactive');
                          setShowStatusMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center justify-between ${statusFilter === 'inactive' ? 'text-indigo-600 font-medium' : 'text-gray-700'
                          }`}
                      >
                        Inactivos
                        {statusFilter === 'inactive' && <div className="w-1.5 h-1.5 rounded-full bg-indigo-600" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Sort by */}
              <div className="relative">
                <button
                  onClick={() => setShowSortMenu(!showSortMenu)}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  <span>Ordenar por</span>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {/* Sort dropdown menu */}
                {showSortMenu && (
                  <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setSortField('name');
                          setSortDirection('asc');
                          setShowSortMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center justify-between ${sortField === 'name' && sortDirection === 'asc' ? 'text-indigo-600 font-medium' : 'text-gray-700'
                          }`}
                      >
                        Nombre (A-Z)
                        {sortField === 'name' && sortDirection === 'asc' && <div className="w-1.5 h-1.5 rounded-full bg-indigo-600" />}
                      </button>
                      <button
                        onClick={() => {
                          setSortField('name');
                          setSortDirection('desc');
                          setShowSortMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center justify-between ${sortField === 'name' && sortDirection === 'desc' ? 'text-indigo-600 font-medium' : 'text-gray-700'
                          }`}
                      >
                        Nombre (Z-A)
                        {sortField === 'name' && sortDirection === 'desc' && <div className="w-1.5 h-1.5 rounded-full bg-indigo-600" />}
                      </button>
                      <button
                        onClick={() => {
                          setSortField('email');
                          setSortDirection('asc');
                          setShowSortMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center justify-between ${sortField === 'email' && sortDirection === 'asc' ? 'text-indigo-600 font-medium' : 'text-gray-700'
                          }`}
                      >
                        Email (A-Z)
                        {sortField === 'email' && sortDirection === 'asc' && <div className="w-1.5 h-1.5 rounded-full bg-indigo-600" />}
                      </button>
                      <button
                        onClick={() => {
                          setSortField('email');
                          setSortDirection('desc');
                          setShowSortMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center justify-between ${sortField === 'email' && sortDirection === 'desc' ? 'text-indigo-600 font-medium' : 'text-gray-700'
                          }`}
                      >
                        Email (Z-A)
                        {sortField === 'email' && sortDirection === 'desc' && <div className="w-1.5 h-1.5 rounded-full bg-indigo-600" />}
                      </button>
                      <button
                        onClick={() => {
                          setSortField('last_sign_in_at');
                          setSortDirection('desc');
                          setShowSortMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center justify-between ${sortField === 'last_sign_in_at' && sortDirection === 'desc' ? 'text-indigo-600 font-medium' : 'text-gray-700'
                          }`}
                      >
                        Última sesión (Reciente)
                        {sortField === 'last_sign_in_at' && sortDirection === 'desc' && <div className="w-1.5 h-1.5 rounded-full bg-indigo-600" />}
                      </button>
                      <button
                        onClick={() => {
                          setSortField('last_sign_in_at');
                          setSortDirection('asc');
                          setShowSortMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center justify-between ${sortField === 'last_sign_in_at' && sortDirection === 'asc' ? 'text-indigo-600 font-medium' : 'text-gray-700'
                          }`}
                      >
                        Última sesión (Antigua)
                        {sortField === 'last_sign_in_at' && sortDirection === 'asc' && <div className="w-1.5 h-1.5 rounded-full bg-indigo-600" />}
                      </button>
                      <button
                        onClick={() => {
                          setSortField('created_at');
                          setSortDirection('desc');
                          setShowSortMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center justify-between ${sortField === 'created_at' && sortDirection === 'desc' ? 'text-indigo-600 font-medium' : 'text-gray-700'
                          }`}
                      >
                        Fecha creación (Reciente)
                        {sortField === 'created_at' && sortDirection === 'desc' && <div className="w-1.5 h-1.5 rounded-full bg-indigo-600" />}
                      </button>
                      <button
                        onClick={() => {
                          setSortField('created_at');
                          setSortDirection('asc');
                          setShowSortMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center justify-between ${sortField === 'created_at' && sortDirection === 'asc' ? 'text-indigo-600 font-medium' : 'text-gray-700'
                          }`}
                      >
                        Fecha creación (Antigua)
                        {sortField === 'created_at' && sortDirection === 'asc' && <div className="w-1.5 h-1.5 rounded-full bg-indigo-600" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Settings */}
              <button className="p-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                <Settings2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-6 mt-4 border-b border-gray-200">
            <button
              onClick={() => setFilter('all')}
              className={`relative pb-3 px-2 text-sm font-semibold transition-colors cursor-pointer after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-[1px] after:h-[3px] after:transition-colors ${filter === 'all'
                ? 'text-blue-600 after:bg-blue-500'
                : 'text-gray-500 after:bg-transparent hover:text-gray-700 hover:after:bg-gray-300'
                }`}
            >
              Todos ({allCount})
            </button>
            <button
              onClick={() => setFilter('superadmin')}
              className={`relative pb-3 px-2 text-sm font-semibold transition-colors cursor-pointer after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-[1px] after:h-[3px] after:transition-colors ${filter === 'superadmin'
                ? 'text-blue-600 after:bg-blue-500'
                : 'text-gray-500 after:bg-transparent hover:text-gray-700 hover:after:bg-gray-300'
                }`}
            >
              Superadmins ({superAdminCount})
            </button>
            <button
              onClick={() => setFilter('organizer')}
              className={`relative pb-3 px-2 text-sm font-semibold transition-colors cursor-pointer after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-[1px] after:h-[3px] after:transition-colors ${filter === 'organizer'
                ? 'text-blue-600 after:bg-blue-500'
                : 'text-gray-500 after:bg-transparent hover:text-gray-700 hover:after:bg-gray-300'
                }`}
            >
              Organizadores ({managerCount})
            </button>
            <button
              onClick={() => setFilter('client')}
              className={`relative pb-3 px-2 text-sm font-semibold transition-colors cursor-pointer after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-[1px] after:h-[3px] after:transition-colors ${filter === 'client'
                ? 'text-blue-600 after:bg-blue-500'
                : 'text-gray-500 after:bg-transparent hover:text-gray-700 hover:after:bg-gray-300'
                }`}
            >
              Clientes ({accountantCount})
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="px-6 py-6">
        {paginatedUsers.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-gray-500">No hay usuarios con este filtro</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-x-visible pb-32">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-10 px-2 py-3"></th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Usuario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Roles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Última Sesión
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Estatus
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedUsers.map((user) => {
                  const isRoot = isProtectedUser(user.email);
                  const isActive = !user.banned_until;
                  const isExpanded = expandedUsers.has(user.id);

                  // Capitalizar y limitar nombre
                  const rawName = user.name || 'Sin nombre';
                  const capitalizedName = rawName.toLowerCase().replace(/(?:^|\s)\S/g, l => l.toUpperCase());
                  const displayName = capitalizedName.length > 32
                    ? capitalizedName.substring(0, 32) + '...'
                    : capitalizedName;

                  // Determinar tipo de cliente basado en customer_type del perfil
                  const customerTypeLabel = user.customer_type === 'egresado_unach'
                    ? 'Egresado UNACH'
                    : user.customer_type === 'publico_general'
                      ? 'Público General'
                      : 'No especificado';

                  const toggleExpand = () => {
                    const newExpanded = new Set(expandedUsers);
                    if (isExpanded) {
                      newExpanded.delete(user.id);
                    } else {
                      newExpanded.add(user.id);
                    }
                    setExpandedUsers(newExpanded);
                  };

                  return (
                    <>
                      <tr key={user.id} className="hover:bg-gray-50">
                        {/* Expand/Collapse */}
                        <td className="w-10 px-2 py-4">
                          <button
                            onClick={toggleExpand}
                            className="p-1 text-gray-400 hover:text-gray-600 cursor-pointer transition-transform duration-200"
                          >
                            <ChevronRight
                              className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                            />
                          </button>
                        </td>

                        {/* Usuario */}
                        <td className="px-6 py-4 whitespace-nowrap text-left">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center">
                                <span className="text-white text-sm font-medium">
                                  {displayName.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {displayName}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Email */}
                        <td className="px-6 py-4 whitespace-nowrap text-left">
                          <div className="text-sm text-gray-900">{user.email}</div>
                        </td>

                        {/* Roles */}
                        <td className="px-6 py-4 text-center">
                          <div className="flex flex-wrap gap-1 justify-center">
                            {user.roles.map((role) => (
                              <span
                                key={role}
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${role === 'superadmin'
                                  ? 'bg-red-100 text-red-800'
                                  : role === 'organizer'
                                    ? 'bg-orange-100 text-orange-800'
                                    : 'bg-green-100 text-green-800'
                                  }`}
                              >
                                {role === 'superadmin'
                                  ? 'Superadmin'
                                  : role === 'organizer'
                                    ? 'Organizador'
                                    : 'Cliente'}
                              </span>
                            ))}
                          </div>
                        </td>

                        {/* Última Sesión */}
                        <td className="px-6 py-4 whitespace-nowrap text-left">
                          <div className="text-sm text-gray-700">
                            {user.last_sign_in_at
                              ? new Date(user.last_sign_in_at).toLocaleDateString('es-ES', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })
                              : 'Nunca'}
                          </div>
                        </td>

                        {/* Estatus */}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                              }`}
                          >
                            {isActive ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                          <div className="flex items-center justify-center gap-2">
                            {/* Edit */}
                            <button
                              onClick={() => openEditUserModal(user)}
                              disabled={isRoot}
                              className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                              title="Editar"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>

                            {/* Delete */}
                            <button
                              onClick={() => handleDeleteUser(user.id, user.email)}
                              disabled={isRoot}
                              className="p-1.5 text-gray-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>

                            {/* More options */}
                            <div className="relative">
                              <button
                                onClick={() =>
                                  setShowActionsMenu(showActionsMenu === user.id ? null : user.id)
                                }
                                className="p-1.5 text-gray-400 hover:text-gray-600 cursor-pointer"
                                title="Más opciones"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>

                              {/* Dropdown menu */}
                              {showActionsMenu === user.id && (
                                <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                                  <div className="py-1">
                                    <button
                                      onClick={() => openResetPasswordModal(user)}
                                      disabled={isRoot}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 cursor-pointer"
                                    >
                                      <KeyRound className="w-4 h-4" />
                                      Resetear Contraseña
                                    </button>
                                    <button
                                      onClick={() => handleShowCurrentPassword(user.id, user.email)}
                                      disabled={isRoot}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 cursor-pointer"
                                    >
                                      <Eye className="w-4 h-4" />
                                      Ver Contraseña
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleToggleUserStatus(
                                          user.id,
                                          user.email,
                                          isActive ? 'ban' : 'unban'
                                        )
                                      }
                                      disabled={isRoot}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 cursor-pointer"
                                    >
                                      <Lock className="w-4 h-4" />
                                      {isActive ? 'Desactivar' : 'Activar'} Usuario
                                    </button>
                                    <button
                                      onClick={() => openEmailCascadeModal(user)}
                                      disabled={isRoot}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 cursor-pointer"
                                    >
                                      <Mail className="w-4 h-4" />
                                      Corregir Email en Cascada
                                    </button>
                                    <div className="border-t border-gray-100 my-1"></div>
                                    <button
                                      onClick={() => setSelectedUser(user)}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 cursor-pointer"
                                    >
                                      <Shield className="w-4 h-4" />
                                      Gestionar Roles
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>

                      {/* Fila expandible con datos adicionales */}
                      {isExpanded && (() => {
                        const isOrganizer = user.roles.includes('organizer') || user.roles.includes('superadmin');
                        const isEgresado = user.customer_type === 'egresado_unach';
                        // Calcular columnas: Teléfono (siempre) + Depto (si organizer) + Tipo Cliente (siempre) + Programa (si egresado)
                        const colCount = 1 + (isOrganizer ? 1 : 0) + 1 + (isEgresado ? 1 : 0);
                        const gridClass = colCount === 4 ? 'grid-cols-4' : colCount === 3 ? 'grid-cols-3' : 'grid-cols-2';

                        return (
                          <tr key={`${user.id}-details`} className="bg-gray-50">
                            <td colSpan={7} className="px-6 py-4">
                              <div className={`grid gap-8 pl-12 ${gridClass} justify-items-center`}>
                                <div className="flex flex-col items-center text-center">
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Teléfono</p>
                                  <p className="text-sm text-gray-900">{user.phone || 'No registrado'}</p>
                                </div>
                                {isOrganizer && (
                                  <div className="flex flex-col items-center text-center">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Departamento</p>
                                    <p className="text-sm text-gray-900">{user.organization_name || 'Sin asignar'}</p>
                                  </div>
                                )}
                                <div className="flex flex-col items-center text-center">
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Tipo de Cliente</p>
                                  <p className="text-sm text-gray-900">{customerTypeLabel}</p>
                                </div>
                                {isEgresado && (
                                  <div className="flex flex-col items-center text-center">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Programa Educativo</p>
                                    <p className="text-sm text-gray-900">{user.graduate_program || 'No especificado'}</p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })()}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Mostrando {startIndex + 1} a {Math.min(startIndex + itemsPerPage, filteredUsers.length)} de{' '}
              {filteredUsers.length} usuarios
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-700">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Drawer para detalles del usuario */}
      <Drawer
        isOpen={selectedUser !== null}
        onClose={() => setSelectedUser(null)}
        title={`Detalles de ${selectedUser?.email || 'Usuario'}`}
      >
        {selectedUser && (
          <div className="space-y-6">
            {/* Información básica */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Información Básica</h3>
              <div className="space-y-2">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Email:</span>
                  <span className="text-sm font-medium text-gray-900">{selectedUser.email}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Nombre:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {selectedUser.name || 'No especificado'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Teléfono:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {selectedUser.phone || 'No especificado'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Organización:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {selectedUser.organization_name || 'No especificado'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Creado:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDate(selectedUser.created_at)}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Última sesión:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDate(selectedUser.last_sign_in_at)}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Estado:</span>
                  <span
                    className={`text-sm font-medium ${selectedUser.banned_until ? 'text-red-600' : 'text-green-600'}`}
                  >
                    {selectedUser.banned_until ? 'Desactivado' : 'Activo'}
                  </span>
                </div>
              </div>
            </div>

            {/* Roles */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Roles Asignados</h3>
              <div className="space-y-2">
                {selectedUser.roles.map((role) => (
                  <div
                    key={role}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <span
                      className={`text-sm font-medium ${role === 'superadmin'
                        ? 'text-red-700'
                        : role === 'organizer'
                          ? 'text-orange-700'
                          : 'text-green-700'
                        }`}
                    >
                      {role}
                    </span>
                    {!isProtectedUser(selectedUser.email) && (
                      <button
                        onClick={() => handleRemoveRole(selectedUser.id, role)}
                        className="text-red-600 hover:text-red-800 text-sm"
                        disabled={isProcessing}
                      >
                        Quitar
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Agregar rol */}
              {!isProtectedUser(selectedUser.email) && (
                <div className="mt-4 space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Agregar Rol</h4>
                  <div className="flex gap-2">
                    {['client', 'organizer', 'superadmin']
                      .filter((role) => !selectedUser.roles.includes(role))
                      .map((role) => (
                        <button
                          key={role}
                          onClick={() => handleAssignRole(selectedUser.id, role)}
                          className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                          disabled={isProcessing}
                        >
                          + {role}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Acciones */}
            {!isProtectedUser(selectedUser.email) && (
              <div className="pt-4 border-t border-gray-200 space-y-2">
                <Button
                  onClick={() => openEditUserModal(selectedUser)}
                  variant="secondary"
                  className="w-full justify-center"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Editar Usuario
                </Button>
                <Button
                  onClick={() =>
                    handleToggleUserStatus(
                      selectedUser.id,
                      selectedUser.email,
                      selectedUser.banned_until ? 'unban' : 'ban'
                    )
                  }
                  variant="secondary"
                  className="w-full justify-center"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {selectedUser.banned_until ? 'Activar' : 'Desactivar'} Usuario
                </Button>
                <Button
                  onClick={() => handleDeleteUser(selectedUser.id, selectedUser.email)}
                  variant="danger"
                  className="w-full justify-center"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar Usuario
                </Button>
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* Modal para crear/editar usuario */}
      <Drawer
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
        title={userFormData.isEdit ? 'Editar Usuario' : 'Crear Usuario'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <Input
              type="email"
              value={userFormData.email}
              onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
              placeholder="usuario@ejemplo.com"
              disabled={userFormData.isEdit}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <Input
              type="text"
              value={userFormData.name}
              onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
              placeholder="Nombre completo"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
            <Input
              type="tel"
              value={userFormData.phone}
              onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })}
              placeholder="10 dígitos"
            />
          </div>

          {!userFormData.isEdit && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contraseña *
                </label>
                <Input
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                  placeholder="Mínimo 6 caracteres"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol *</label>
                <select
                  value={userFormData.role}
                  onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value })}
                  className="input-field w-full"
                >
                  <option value="client">Cliente</option>
                  <option value="organizer">Organizador</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </div>
            </>
          )}

          {userFormData.role === 'organizer' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organización</label>
              <Input
                type="text"
                value={userFormData.organization_name}
                onChange={(e) =>
                  setUserFormData({ ...userFormData, organization_name: e.target.value })
                }
                placeholder="Nombre de la organización"
              />
            </div>
          )}

          {!userFormData.isEdit && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sendEmail"
                checked={userFormData.sendEmail}
                onChange={(e) => setUserFormData({ ...userFormData, sendEmail: e.target.checked })}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label htmlFor="sendEmail" className="text-sm text-gray-700">
                Enviar credenciales por email
              </label>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button
              onClick={handleSaveUser}
              disabled={isProcessing}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
            >
              {isProcessing ? 'Guardando...' : userFormData.isEdit ? 'Actualizar' : 'Crear'}
            </Button>
            <Button
              onClick={() => setShowUserModal(false)}
              variant="secondary"
              disabled={isProcessing}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </Drawer>

      {/* Modal para resetear contraseña */}
      <Drawer
        isOpen={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
        title="Resetear Contraseña"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
            <Input type="text" value={resetPasswordData.userEmail} disabled />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nueva Contraseña *
            </label>
            <Input
              type="password"
              value={resetPasswordData.newPassword}
              onChange={(e) =>
                setResetPasswordData({ ...resetPasswordData, newPassword: e.target.value })
              }
              placeholder="Mínimo 6 caracteres"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="sendEmailReset"
              checked={resetPasswordData.sendEmail}
              onChange={(e) =>
                setResetPasswordData({ ...resetPasswordData, sendEmail: e.target.checked })
              }
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <label htmlFor="sendEmailReset" className="text-sm text-gray-700">
              Enviar nueva contraseña por email
            </label>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              onClick={() => handleResetPassword(false)}
              disabled={isProcessing}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
            >
              {isProcessing ? 'Reseteando...' : 'Resetear'}
            </Button>
            <Button
              onClick={() => setShowResetPasswordModal(false)}
              variant="secondary"
              disabled={isProcessing}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </Drawer>

      {/* Modal para mostrar contraseña generada */}
      <Drawer
        isOpen={showGeneratedPasswordModal}
        onClose={() => setShowGeneratedPasswordModal(false)}
        title="Contraseña Generada"
      >
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              <strong>Importante:</strong> Esta es la única vez que se mostrará esta contraseña.
              Guárdala en un lugar seguro.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
            <Input type="text" value={generatedPasswordUserEmail} disabled />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
            <div className="relative">
              <Input type="text" value={generatedPassword} disabled className="pr-20" />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(generatedPassword);
                  success('Contraseña copiada al portapapeles');
                }}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Copiar
              </button>
            </div>
          </div>

          <Button
            onClick={() => setShowGeneratedPasswordModal(false)}
            className="w-full bg-indigo-600 hover:bg-indigo-700"
          >
            Entendido
          </Button>
        </div>
      </Drawer>

      {/* Modal para cascada de email */}
      <Drawer
        isOpen={showEmailCascadeModal}
        onClose={() => setShowEmailCascadeModal(false)}
        title="Corregir Email en Cascada"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              Esta acción actualizará el email en todas las tablas relacionadas (purchases,
              email_logs, etc.)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Actual</label>
            <Input type="text" value={emailCascadeData.oldEmail} disabled />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Nuevo *</label>
            <Input
              type="email"
              value={emailCascadeData.newEmail}
              onChange={(e) => setEmailCascadeData({ ...emailCascadeData, newEmail: e.target.value })}
              placeholder="nuevo@ejemplo.com"
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              onClick={handleEmailCascadeFix}
              disabled={isProcessing}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
            >
              {isProcessing ? 'Ejecutando...' : 'Ejecutar Cascada'}
            </Button>
            <Button
              onClick={() => setShowEmailCascadeModal(false)}
              variant="secondary"
              disabled={isProcessing}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </Drawer>

      {/* Click outside to close menus */}
      {(showActionsMenu || showSortMenu || showStatusMenu) && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => {
            setShowActionsMenu(null);
            setShowSortMenu(false);
            setShowStatusMenu(false);
          }}
        />
      )}
    </div>
  );
}
